const YOUTUBE_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/

export function parseYoutubeVideoId(input: string): string | null {
  const trimmed = String(input || '').trim()
  if (!trimmed) return null
  if (YOUTUBE_ID_PATTERN.test(trimmed)) return trimmed

  try {
    const url = trimmed.startsWith('http') ? new URL(trimmed) : new URL(`https://${trimmed}`)
    const host = url.hostname.replace(/^www\./, '')

    if (host === 'youtu.be') {
      const id = url.pathname.replace(/^\//, '').split('/')[0]
      return YOUTUBE_ID_PATTERN.test(id) ? id : null
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (url.pathname.startsWith('/embed/')) {
        const id = url.pathname.split('/')[2]
        return YOUTUBE_ID_PATTERN.test(id) ? id : null
      }
      if (url.pathname.startsWith('/shorts/')) {
        const id = url.pathname.split('/')[2]
        return YOUTUBE_ID_PATTERN.test(id) ? id : null
      }
      const fromQuery = url.searchParams.get('v')
      return fromQuery && YOUTUBE_ID_PATTERN.test(fromQuery) ? fromQuery : null
    }
  } catch {
    // ignore
  }

  const watchMatch = trimmed.match(/(?:v=|\/embed\/|youtu\.be\/|\/shorts\/)([a-zA-Z0-9_-]{11})/)
  return watchMatch?.[1] && YOUTUBE_ID_PATTERN.test(watchMatch[1]) ? watchMatch[1] : null
}

export function isValidYoutubeUrl(input: string): boolean {
  return parseYoutubeVideoId(input) !== null
}

export function toYoutubeEmbedUrl(
  videoId: string,
  options?: { autoplay?: boolean; muted?: boolean },
): string {
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
  })
  if (options?.autoplay) {
    params.set('autoplay', '1')
    if (options.muted !== false) {
      params.set('mute', '1')
    }
  } else if (options?.muted === true) {
    params.set('mute', '1')
  }
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`
}

/** Pick a YouTube thumbnail. `quality` defaults to 'mq' (320x180). */
export function toYoutubeThumbnailUrl(
  videoId: string,
  quality: 'default' | 'mq' | 'hq' | 'sd' | 'maxres' = 'mq'
): string {
  const map = {
    default: 'default',
    mq: 'mqdefault',
    hq: 'hqdefault',
    sd: 'sddefault',
    maxres: 'maxresdefault',
  } as const
  return `https://i.ytimg.com/vi/${videoId}/${map[quality]}.jpg`
}
